import { zodResolver } from '@hookform/resolvers/zod';
import { CaretLeftIcon, InfoIcon } from '@phosphor-icons/react';
import { Input } from 'antd';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { Button } from '@/components/common/button';
import { Tooltip } from '@/components/common/tooltip';
import { useToast } from '@/components/feedback/toast';
import {
  AvailableCountField,
  CheckboxField,
  FormField,
  MultiSelectField,
  NumberField,
  RadioGroupField,
  SelectField,
  TextAreaField,
  UploadField,
} from '@/components/forms';
import { onlyEnabled } from '@/config/preview-features';
import { ApiError } from '@/lib/api/errors';
import type { UploadFn } from '@/lib/upload';
import { UPLOAD_PRESETS } from '@/lib/upload';
import type { PreviewFeature } from '@/types';

import { deviceFormSchema, maskDeviceName } from '../schemas';
import type { DeviceFormValues } from '../types';

import styles from './device-form.module.scss';

/** CropX is outside the release scope (ADR-0034, ADO 121238); `onlyEnabled` drops it at render. */
const DEVICE_TYPE_OPTIONS: { label: string; value: string; feature?: PreviewFeature }[] = [
  { label: 'DM Device', value: 'dm_device' },
  { label: 'CropX', value: 'cropx', feature: 'cropx' },
  { label: 'Other', value: 'other' },
];

const SUBSCRIPTION_TOOLTIP = 'Subscription duration is fixed at 1 year. Only the price can vary.';
const BUNDLE_ONLY_LABEL =
  'This device is not available for purchase separately. Only sold in bundles.';

export type DeviceFormProps = {
  title: string;
  submitLabel: string;
  categoryOptions: { value: string; label: string }[];
  componentOptions: { value: string; label: string }[];
  defaultValues?: Partial<DeviceFormValues>;
  hideTypeSelector?: boolean;
  imageUploader: UploadFn;
  guideUploader: UploadFn;
  onSubmit: (values: DeviceFormValues) => Promise<void>;
  onCancel: () => void;
};

export function DeviceForm({
  title,
  submitLabel,
  categoryOptions,
  componentOptions,
  defaultValues,
  hideTypeSelector,
  imageUploader,
  guideUploader,
  onSubmit,
  onCancel,
}: DeviceFormProps) {
  const toast = useToast();
  const form = useForm<DeviceFormValues>({
    resolver: zodResolver(deviceFormSchema),
    mode: 'onTouched',
    defaultValues: {
      name: '',
      deviceType: 'dm_device',
      availableCount: undefined,
      categoryId: '',
      warrantyMonths: undefined,
      price: undefined,
      installationPrice: undefined,
      subscriptionPrice: null,
      componentIds: [],
      notAvailableForPurchase: false,
      description: '',
      specifications: '',
      images: [],
      installationGuide: [],
      ...defaultValues,
    } as DeviceFormValues,
  });

  // RHF's own `formState.isValid` stays true (unvalidated) until a field is touched or
  // `trigger()` runs, so Save would render enabled on a blank create form. Driving Save from a
  // silent schema check instead — rather than calling `form.trigger()` on mount — gets the
  // correct disabled state without populating every empty required field's error before the
  // admin has touched anything.
  const [isFormValid, setIsFormValid] = useState(
    () => deviceFormSchema.safeParse(form.getValues()).success,
  );
  useEffect(() => {
    const subscription = form.watch((values) => {
      setIsFormValid(deviceFormSchema.safeParse(values).success);
    });
    return () => subscription.unsubscribe();
  }, [form]);

  const handleSubmit = async (values: DeviceFormValues) => {
    try {
      await onSubmit(values);
    } catch (error) {
      if (error instanceof ApiError && error.code === 'DEVICE_ALREADY_EXISTS') {
        form.setError('name', { message: 'Device already exists' });
        return;
      }
      toast.error({
        title:
          error instanceof ApiError ? error.message : 'Something went wrong. Please try again.',
      });
    }
  };

  const deviceType = form.watch('deviceType');
  const isOther = deviceType === 'other';

  return (
    <form className={styles.form} onSubmit={form.handleSubmit(handleSubmit)} noValidate>
      <header className={styles.header}>
        <div className={styles.titleGroup}>
          <button type="button" aria-label="Back" className={styles.backBtn} onClick={onCancel}>
            <CaretLeftIcon size={20} weight="bold" />
          </button>
          <h1 className={styles.title}>{title}</h1>
        </div>
        <div className={styles.actions}>
          <Button htmlType="button" variant="outline" onClick={onCancel} className={styles.pill}>
            Cancel
          </Button>
          <Button
            htmlType="submit"
            variant="neutral"
            disabled={!isFormValid || form.formState.isSubmitting}
            loading={form.formState.isSubmitting}
            className={styles.pill}
          >
            {submitLabel}
          </Button>
        </div>
      </header>

      {!hideTypeSelector && (
        <div className={styles.typeRow}>
          <RadioGroupField
            name="deviceType"
            control={form.control}
            direction="horizontal"
            options={onlyEnabled(DEVICE_TYPE_OPTIONS)}
          />
        </div>
      )}

      <div className={styles.grid}>
        <FormField name="name" control={form.control} label="Name" required>
          {({ field, fieldState }) => (
            <Input
              id="name"
              placeholder="Enter Name"
              status={fieldState.error ? 'error' : undefined}
              value={field.value}
              ref={field.ref}
              onBlur={field.onBlur}
              onChange={(e) => field.onChange(maskDeviceName(e.target.value))}
            />
          )}
        </FormField>

        <AvailableCountField
          name="availableCount"
          control={form.control}
          label="Available Devices"
          required
        />

        <SelectField
          name="categoryId"
          control={form.control}
          label="Category"
          required
          placeholder="Select"
          options={categoryOptions}
        />

        <NumberField
          name="warrantyMonths"
          control={form.control}
          label="Warranty (In months)"
          placeholder="Enter Warranty"
          min={0}
          step={1}
          precision={0}
          maxIntegerDigits={4}
        />

        <NumberField
          name="price"
          control={form.control}
          label="Price ($)"
          required
          placeholder="Enter Price"
          min={0}
          step={0.01}
          precision={2}
          maxIntegerDigits={10}
        />

        <NumberField
          name="installationPrice"
          control={form.control}
          label="Installation Fee ($)"
          placeholder="Enter Price"
          min={0}
          step={0.01}
          precision={2}
          maxIntegerDigits={10}
        />

        <MultiSelectField
          name="componentIds"
          control={form.control}
          label="Applicable Components"
          required={!isOther}
          placeholder="Select"
          options={componentOptions}
          maxTagCount={2}
          maxTagPlaceholder={(omitted) => (
            <Tooltip
              appearance="dark"
              placement="bottom"
              title={
                <span className={styles.moreList}>
                  {omitted.map((o) => (
                    <span key={String(o.value)}>{o.label}</span>
                  ))}
                </span>
              }
            >
              <span className={styles.moreTag}>+{omitted.length}</span>
            </Tooltip>
          )}
        />

        {!isOther && (
          <NumberField
            name="subscriptionPrice"
            control={form.control}
            label={
              <span className={styles.labelWithInfo}>
                Subscription Price ($)
                <Tooltip title={SUBSCRIPTION_TOOLTIP} placement="bottomLeft" appearance="dark">
                  <span role="img" aria-label="Subscription price info" className={styles.infoIcon}>
                    <InfoIcon size={14} aria-hidden />
                  </span>
                </Tooltip>
              </span>
            }
            required={deviceType === 'dm_device'}
            placeholder="Enter Price"
            min={0}
            step={0.01}
            precision={2}
            maxIntegerDigits={10}
          />
        )}

        {!isOther && (
          <div className={styles.full}>
            <CheckboxField
              name="notAvailableForPurchase"
              control={form.control}
              label={BUNDLE_ONLY_LABEL}
            />
          </div>
        )}

        <div className={styles.full}>
          <TextAreaField
            name="description"
            control={form.control}
            label="Description"
            required
            placeholder="Enter"
            rows={5}
            maxLength={2000}
            showCount
            style={{ resize: 'none' }}
          />
        </div>

        <div className={styles.full}>
          <TextAreaField
            name="specifications"
            control={form.control}
            label="Specifications"
            required
            placeholder="Enter"
            rows={5}
            maxLength={2000}
            showCount
            style={{ resize: 'none' }}
          />
        </div>

        <div className={styles.uploads}>
          {/* Figma 12:163611 / 12:162336 (ADO 113313): once a file is attached the drop zone
              collapses into its own card carrying the thumbnails, an "Upload more" trigger and
              the count — it is not a permanent drop zone with the files stacked underneath. */}
          <UploadField
            name="images"
            control={form.control}
            label="Images"
            required
            showCounter
            collapseWhenFilled
            columns={3}
            addMoreLabel="Upload more images (JPG, JPEG or PNG)"
            {...UPLOAD_PRESETS.componentImages}
            onUpload={imageUploader}
          />
          <UploadField
            name="installationGuide"
            control={form.control}
            label="Installation Guide"
            required
            showCounter
            collapseWhenFilled
            addMoreLabel="Upload more files (Videos or PDF/DOC)"
            {...UPLOAD_PRESETS.installationGuide}
            onUpload={guideUploader}
          />
        </div>
      </div>
    </form>
  );
}
